import {last} from 'lodash';
import {DateTime} from 'luxon';

import {readApiModel} from '../../libs/shared/core/src/helpers/api/apiHelpers';
import {
  build12Or24hOptionsForFormat,
  TimeFormatsEnum,
} from '../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {reportToBugsnag} from '../../libs/shared/core/src/services/bugsnag/bugsnag';
import {
  ApiCalendarNotificationLinkParameters,
  serializeCalendarNotificationLinkParameters,
} from '../models/api/calendarNotificationLinkModel';
import {CalendarEventParameters} from '../store/panels/calendars/calendarsPanelState';

/**
 * Builds the calendar event time text to be shown in a reminder notification:
 * All day: "Sept 8" or "Sept 8 - Sept 9"
 * Standard: 11:00 AM - 11:30 PM
 */
export function buildCalendarReminderTimeText(
  startDateTime: DateTime,
  endDateTime: DateTime,
  isAllDay: boolean,
  timeFormat: TimeFormatsEnum,
) {
  const allDayFormat: Intl.DateTimeFormatOptions = {
    month: 'short',
    day: 'numeric',
  };

  const standardFormat: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: 'numeric',
    ...build12Or24hOptionsForFormat(timeFormat),
  };

  const difference = endDateTime.diff(startDateTime, 'days');
  if (isAllDay && difference.as('days') <= 1) {
    return startDateTime.toLocaleString(allDayFormat);
  }

  if (isAllDay) {
    return `${startDateTime.toLocaleString(allDayFormat)} - ${endDateTime
      .minus({days: 1})
      .toLocaleString(allDayFormat)}`;
  }

  return `${startDateTime.toLocaleString(standardFormat)} - ${endDateTime.toLocaleString(standardFormat)}`;
}

export const calendarNotificationLinkPrefix = '/open-calendar-event/';
export function buildCalendarNotificationLink(
  calendarEventParameters: CalendarEventParameters,
  focusedDate: number,
) {
  const payload = serializeCalendarNotificationLinkParameters({
    focusedDate,
    calendarEventParameters,
  });

  // Stringify the payload and then base64 encode for the URL.
  return `${calendarNotificationLinkPrefix}${btoa(JSON.stringify(payload))}`;
}

export function tryParseCalendarNotificationLink(link: string) {
  if (!link.startsWith(calendarNotificationLinkPrefix)) {
    return null;
  }

  const encodedPayload = last(link.split('/'));
  if (!encodedPayload) {
    return null;
  }

  try {
    // Base64 decode and parse the stringified JSON.
    const src = JSON.parse(atob(encodedPayload));
    return readApiModel(ApiCalendarNotificationLinkParameters, src);
  } catch (e) {
    reportToBugsnag(new Error(`Failed to parse calendar notification link: ${link}`));
    return null;
  }
}
